import {useRef, useEffect, useState} from 'react';
import Dropdown from '../_dropdown';
import Icon from '../_icon';

import {
  defaultDigestFrequency,
  defaultDigestDay,
  defaultDigestHour,
  reviewFrequencyOptions,
  reviewHourOptions,
  reviewDayOptions,
  defaultDigestSendOnceHours
} from '../../modules/digest_utils';

const DigestFrequencySettings = ({
  reviewFrequency,
  reviewDay,
  reviewHour,
  sendTime,
  sendOnceHours,
  onSetSendOnceHours,
  onSetReviewFrequency,
  onSetReviewDay,
  onSetReviewHour,
  onResetToDefaults
}) => {
  const onceInputRef = useRef(null);
  const [showInfo, setShowInfo] = useState(false);
  const [hours, setHours] = useState(sendOnceHours || defaultDigestSendOnceHours);
  const toggleShowInfo = () => setShowInfo(!showInfo);

  useEffect(() => {
    setHours(sendOnceHours || defaultDigestSendOnceHours);
  }, [sendOnceHours]);

  const handleOnChange = e => {
    setHours(e?.target?.value.match(/[0-9]*/) || '');
  };

  const handleSave = () => {
    onSetSendOnceHours(hours);
  };

  const handleSetReviewFrequency = value => {
    onSetReviewFrequency(value, () => {
      if(value === 'once') {
        onceInputRef?.current?.select();
      }
    });
  };

  const hideDayOptions = reviewFrequency === 'monthly' ||
  reviewFrequency === 'daily' ||
  reviewFrequency === 'once' ||
  reviewFrequency === 'never';
  const hideHourOptions = reviewFrequency === 'once' ||
  reviewFrequency === 'never';
  let hourInputRegion = null;

  if(reviewFrequency === 'once') {
    hourInputRegion = (<div className="hours-input">
      <h6>Hours from Now</h6>
      <div>
        <div className="digest-send-once-group">
          <input
            ref={onceInputRef}
            type="text"
            className="form-control"
            value={hours}
            onChange={handleOnChange} />
          <input className="faux-form_submit" type="submit" />
        </div>
        <div className="button" onClick={handleSave}>
          Save
        </div>
      </div>
    </div>);
  }

  return (
    <div>
      <div className="digest-setting-header-with-info" data-tracking-id="digest-setting-delivery-schedule-label">
        <h4 className="u-pt-m"><strong>Delivery Schedule</strong></h4>
        <Icon icon="info-outline" width={18} height={18} onClick={toggleShowInfo} />
      </div>
      {showInfo && (
        <div className="help-block u-mb-xs">
          Set the delivery frequency and timing.
          Scheduling is always based on <a href="https://www.timeanddate.com/worldclock/timezone/utc">Universal Time</a> to
          avoid time-zone dependent behaviors.<br />
          <br />
          While email will be <strong>sent</strong> based on this schedule, <strong>receiving</strong> is
          subject to delivery delays that can be up to a few hours.<br />
        </div>
      )}
      <div className="settings-review_frequency-options u-mb-m">
        <div style={{margin: 0}}>
          <div className="ui-dropdown-panel ui-dropdown-panel--left digest-schedule u-pt-xxs" data-testid="company-settings-digest-frequency">
            <div data-testid="digest-schedule_frequency">
              <h6>Frequency</h6>
              <Dropdown
                label=""
                displayLabel="Select..."
                options={reviewFrequencyOptions}
                selectedValue={reviewFrequency}
                keyPrefix="group"
                className="btn secondary"
                containerClass="ui-dropdown--left"
                alignMenuLeft={true}
                onOptionClick={handleSetReviewFrequency} />
            </div>
            {!hideDayOptions
              ? <div>
                <h6>Day</h6>
                <Dropdown
                  label=""
                  displayLabel="Select..."
                  options={reviewDayOptions}
                  selectedValue={reviewDay}
                  keyPrefix="group"
                  className="btn secondary"
                  containerClass="ui-dropdown--left"
                  alignMenuLeft={true}
                  onOptionClick={onSetReviewDay} />
              </div>
              : null}
            {!hideHourOptions
              ? <div>
                <h6>Hour</h6>
                <Dropdown
                  label=""
                  displayLabel="Select..."
                  options={reviewHourOptions}
                  selectedValue={reviewHour}
                  keyPrefix="group"
                  className="btn secondary"
                  containerClass="ui-dropdown--left"
                  alignMenuLeft={true}
                  onOptionClick={onSetReviewHour} />
              </div>
              : null}
            {hourInputRegion}
            <div className="digest-schedule-reset" title="Reset to defaults" onClick={onResetToDefaults}>
              <Icon icon="refresh" />
            </div>
          </div>
          {sendTime
            ? <div className="digest-schedule-time">
              <span className="head">Next scheduled send:</span>
              <span className="time">
                <time dateTime={sendTime.toDate()}>{`${sendTime.format('LLLL')} ${Intl.DateTimeFormat().resolvedOptions().timeZone}`}</time>
              </span>
            </div>
            : <div className="digest-schedule-time">
              <span className="head">Scheduled sending disabled, hit <strong>Send Now</strong> in Digest Editor when ready to send.</span>
            </div>
          }
        </div>
      </div>
    </div>
  );
};

DigestFrequencySettings.propTypes = {
  reviewFrequency: PropTypes.string,
  reviewDay: PropTypes.string,
  reviewHour: PropTypes.number,
  sendTime: PropTypes.object,
  sendOnceHours: PropTypes.any,
  onSetSendOnceHours: PropTypes.func,
  onSetReviewFrequency: PropTypes.func,
  onSetReviewDay: PropTypes.func,
  onSetReviewHour: PropTypes.func,
  onResetToDefaults: PropTypes.func
};

DigestFrequencySettings.defaultProps = {
  reviewFrequency: defaultDigestFrequency,
  reviewDay: defaultDigestDay,
  reviewHour: defaultDigestHour,
  sendTime: null,
  sendOnceHours: null,
  onSetSendOnceHours() {},
  onSetReviewFrequency() {},
  onSetReviewDay() {},
  onSetReviewHour() {},
  onResetToDefaults() {}
};

export default DigestFrequencySettings;
